<template>
  <div class="fm-page-shell">
    <!-- ===== CONTEXTUAL RAIL ===== -->
    <aside class="fm-rail">
      <div class="fm-rail-label">Users</div>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': roleView === 'all' }" @click="roleView = 'all'">
        <span class="fm-view-dot" style="background:#94A3B8"></span>All
        <span class="fm-view-count">{{ allCount }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': roleView === 'admins' }" @click="roleView = 'admins'">
        <span class="fm-view-dot" style="background:#3B82F6"></span>Admins
        <span class="fm-view-count">{{ adminCount }}</span>
      </button>
      <button type="button" class="fm-view" :class="{ 'fm-view--active': roleView === 'members' }" @click="roleView = 'members'">
        <span class="fm-view-dot" style="background:#8B5CF6"></span>Members
        <span class="fm-view-count">{{ memberCount }}</span>
      </button>
    </aside>

    <!-- ===== MAIN ===== -->
    <main class="fm-main">
      <b-overlay :show="loading" spinner small>
        <div v-if="notAuthorized" class="fm-page">
          <div class="fm-empty">You are not authorized to view this page</div>
        </div>
        <div v-else>
          <!-- Section tabs: Users / Invitations -->
          <div class="fm-tabs">
            <button type="button" class="fm-tab" :class="{ 'fm-tab--active': activeTab === 'users' }" @click="activeTab = 'users'">
              <i class="fa-solid fa-users"></i>Users
            </button>
            <button type="button" class="fm-tab" :class="{ 'fm-tab--active': activeTab === 'invitations' }" @click="activeTab = 'invitations'">
              <i class="fa-solid fa-envelope"></i>Invitations
            </button>
          </div>

          <!-- ===== USERS TAB ===== -->
          <div v-if="activeTab === 'users'" class="fm-page">
            <div class="fm-page-head">
              <div class="fm-page-title">Users</div>
              <button class="fm-btn fm-btn--primary" @click="showInviteUserModal"><i class="fa-solid fa-plus"></i>Invite User</button>
            </div>
            <div class="fm-page-desc">Manage members, roles and access across your organization.</div>

            <div class="fm-toolbar">
              <div class="fm-search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <b-form-input type="search" v-model="searchQuery" placeholder="Search users..."></b-form-input>
              </div>
              <div v-if="totalCount > perPage" class="fm-toolbar-pager">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalCount"
                  :per-page="perPage"
                  size="sm"
                  @change="handlePageChange"
                />
              </div>
            </div>

            <div v-if="displayedUsers.length > 0" class="fm-list">
              <div v-for="user in displayedUsers" :key="user.userId" class="fm-row" @click="openRow(user)">
                <div class="fm-row-main">
                  <div class="fm-row-top">
                    <span class="fm-row-name">{{ user.name }}</span>
                    <i class="fa-regular fa-copy fm-row-copy"></i>
                    <span v-if="user.role" class="fm-tag">{{ user.role }}</span>
                  </div>
                </div>
                <span v-if="user.email" class="fm-value-pill">{{ user.email }}</span>
                <div class="fm-status-cell">
                  <span class="fm-status-pill" :class="user.status === 'Active' ? 'fm-status-pill--suc' : 'fm-status-pill--err'">
                    <span class="fm-dot"></span>{{ user.status === 'Active' ? 'Active' : 'Inactive' }}
                  </span>
                </div>
                <button type="button" class="fm-toggle" :class="{ 'fm-toggle--on': user.status === 'Active' }"
                        @click.stop="changeUserStatus(user.userId)">
                  <span class="fm-toggle-knob"></span>
                </button>
              </div>
            </div>
            <div v-else-if="users.length > 0 && displayedUsers.length === 0" class="fm-empty">
              No users match your search criteria.
            </div>
            <div v-else class="fm-empty">No users yet.</div>

          </div>

          <!-- ===== INVITATIONS TAB ===== -->
          <div v-else class="fm-page">
            <div class="fm-page-head">
              <div class="fm-page-title">Invited Users</div>
              <button
                v-if="invitationStatus.invitations_allowed"
                class="fm-btn fm-btn--primary"
                @click="showInviteUserModal"
              >
                <i class="fa-solid fa-plus"></i>Invite User
              </button>
              <div v-else class="okta-status-indicator">
                <i class="fa fa-shield-alt text-info mr-2"></i>
                <span class="text-muted">SSO Authentication Active</span>
              </div>
            </div>
            <div class="fm-page-desc">Pending invitations to your organization.</div>

            <div v-if="invitedUsers && invitedUsers.length > 0" class="fm-list">
              <div v-for="invite in invitedUsers" :key="invite.email" class="fm-row">
                <div class="fm-row-main">
                  <div class="fm-row-top">
                    <span class="fm-row-name">{{ invite.email }}</span>
                    <span v-if="invite.role" class="fm-tag">{{ invite.role }}</span>
                  </div>
                </div>
                <div class="fm-status-cell">
                  <span class="fm-status-pill" :class="'fm-status-pill--' + invitedTone(invite.status)">
                    <span v-if="invitedTone(invite.status) !== 'neu'" class="fm-dot"></span>{{ invite.status }}
                  </span>
                </div>
                <i class="fa-solid fa-angle-right fm-chev"></i>
              </div>
            </div>
            <div v-else class="fm-empty">
              <div v-if="invitationStatus.invitations_allowed">
                No invited users yet.
                <div class="mt-3">
                  <button class="fm-btn fm-btn--primary" @click="showInviteUserModal"><i class="fa-solid fa-plus"></i>Invite User</button>
                </div>
              </div>
              <div v-else>
                <div class="text-info mb-2">Single Sign-On Active</div>
                <div>User management is handled through your SSO provider. New users will appear here automatically when they first sign in.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Invite User Modal -->
        <b-modal v-model="showModal" title="Invite User" hide-footer>
          <b-form @submit.stop.prevent="handleInviteUser">
            <b-form-group label="Email" label-for="user-email">
              <b-form-input id="user-email" v-model="newUser.email" type="email" required></b-form-input>
            </b-form-group>
            <div class="text-center mt-4">
              <b-button type="submit" variant="primary">Invite</b-button>
            </div>
          </b-form>
        </b-modal>
      </b-overlay>
    </main>

    <!-- ===== DETAIL DRAWER ===== -->
    <detail-drawer
      :open="drawerOpen"
      kind="User"
      :title="selected.name || ''"
      :status="selected.status === 'Active' ? 'Active' : 'Inactive'"
      :tone="selected.status === 'Active' ? 'suc' : 'err'"
      :fields="drawerFields"
      @close="closeDrawer">
      <template #footer>
        <button class="fm-btn fm-btn--primary" @click="drawerAction('role')">
          <i class="fa-solid fa-user-pen"></i>Change Role
        </button>
        <button class="fm-btn fm-btn--ghost" @click="drawerAction('status')">
          <i class="fa-solid" :class="selected.status === 'Active' ? 'fa-ban' : 'fa-check-circle'"></i>{{ selected.status === 'Active' ? 'Deactivate' : 'Activate' }}
        </button>
      </template>
    </detail-drawer>
  </div>
</template>

<script>
import apiClient from '@/http';
import DetailDrawer from '@/components/DetailDrawer.vue';
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BOverlay,
  BPagination,
} from 'bootstrap-vue';

export default {
  components: {
    DetailDrawer,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BPagination,
  },
  data() {
    return {
      showModal: false,
      newUser: {
        email: ''
      },
      users: [],
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      invitedUsers: [],
      loading: false,
      notAuthorized: false,
      searchQuery: '',
      activeTab: 'users',
      roleView: 'all',
      drawerOpen: false,
      selected: {},
      invitationStatus: {
        invitations_allowed: true,
        reason: '',
        okta_enabled: false,
        okta_domain: ''
      },
    };
  },
  methods: {
    openRow(user) {
      this.selected = user;
      this.drawerOpen = true;
    },
    closeDrawer() {
      this.drawerOpen = false;
    },
    drawerAction(type) {
      const user = this.selected;
      this.closeDrawer();
      if (type === 'role') {
        this.changeUserRole(user.userId);
      } else if (type === 'status') {
        this.changeUserStatus(user.userId);
      }
    },
    invitedTone(status) {
      const s = (status || '').toLowerCase();
      if (s === 'accepted' || s === 'active') return 'suc';
      if (s === 'expired' || s === 'declined' || s === 'revoked') return 'err';
      if (s === 'pending' || s === 'invited') return 'warn';
      return 'neu';
    },
    showInviteUserModal() {
      if (!this.invitationStatus.invitations_allowed) {
        this.$bvToast.toast(this.invitationStatus.reason, {
          title: 'User Invitations Disabled',
          variant: 'warning',
          solid: true
        });
        return;
      }
      this.showModal = true;
    },
    async handleInviteUser() {
      try {
        const response = await apiClient.post('/users/invitations', this.newUser);
        this.invitedUsers.push(response.data);
        this.newUser = {email: ''};
        this.showModal = false;
        this.$bvToast.toast('User invited successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        // Refresh the invited users list
        this.fetchInvitedUsers();
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error inviting user';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    async fetchInvitationStatus() {
      try {
        const response = await apiClient.get('/users/invitation-status');
        this.invitationStatus = response.data;
      } catch (error) {
        console.error('Error fetching invitation status:', error);
        // Default to allowing invitations if there's an error
        this.invitationStatus = {
          invitations_allowed: true,
          reason: 'Unable to determine invitation status',
          okta_enabled: false,
          okta_domain: ''
        };
      }
    },
    async changeUserRole(userId) {
      try {
        const response = await apiClient.patch(`/users/${userId}/role`);
        await this.fetchUsers();
        this.$bvToast.toast('User role updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error updating user role';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },
    async changeUserStatus(userId) {
      try {
        const response = await apiClient.patch(`/users/${userId}/status`);
        await this.fetchUsers();
        this.$bvToast.toast('User status updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        const errorMessage = error.response && error.response.data ? error.response.data : 'Error updating user status';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    async fetchUsers() {
      this.loading = true;
      try {
        const response = await apiClient.get(`/users?page=${this.currentPage}&per_page=${this.perPage}`);
        this.users = response.data.users.map(user => ({
          name: user.Name || 'N/A',  // Handle empty name
          email: user.Email,
          role: user.Role,
          userId: user.UserId,
          status: user.Status || 'Active'  // Assign default status if not provided
        }));
        this.totalCount = response.data.total_count; // The total number of users
      } catch (error) {
        if (error.response.status === 403) {
          this.notAuthorized = true;
        } else {
          const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching users';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchUsers(); // Fetch users for the selected page
    },
    async fetchInvitedUsers() {
      this.loading = true;
      try {
        const response = await apiClient.get('/users/invitations');
        this.invitedUsers = response.data.map(user => ({
          email: user.Email,
          role: user.Role,
          userId: user.UserId,
          status: user.Status
        }));
      } catch (error) {
        if (error.response.status === 403) {
          this.notAuthorized = true;
        } else {
          const errorMessage = error.response && error.response.data ? error.response.data : 'Error fetching invited users';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        }
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchUsers();
    this.fetchInvitedUsers();
    this.fetchInvitationStatus();
  },
  computed: {
    isAdminRole() {
      return (role) => (role || '').toLowerCase().includes('admin');
    },
    filteredUsers() {
      if (!this.searchQuery) {
        return this.users;
      }

      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user =>
        (user.name && user.name.toLowerCase().includes(query)) ||
        user.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query) ||
        (user.status && user.status.toLowerCase().includes(query))
      );
    },
    displayedUsers() {
      let list = this.filteredUsers;
      if (this.roleView === 'admins') {
        list = list.filter(u => (u.role || '').toLowerCase().includes('admin'));
      } else if (this.roleView === 'members') {
        list = list.filter(u => !(u.role || '').toLowerCase().includes('admin'));
      }
      return list;
    },
    allCount() {
      return this.users.length;
    },
    adminCount() {
      return this.users.filter(u => (u.role || '').toLowerCase().includes('admin')).length;
    },
    memberCount() {
      return this.users.filter(u => !(u.role || '').toLowerCase().includes('admin')).length;
    },
    drawerFields() {
      const u = this.selected;
      if (!u || !u.name) return [];
      return [
        {label: 'Email', value: u.email, mono: true},
        {label: 'Role', value: u.role},
        {label: 'Status', value: u.status === 'Active' ? 'Active' : 'Inactive'},
        {label: 'Last active', value: u.lastActive || u.LastActive || 'N/A'},
      ];
    }
  }
};
</script>

<style scoped>
.okta-status-indicator {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}
</style>
